import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMyAddresses, upsertAddress, deleteAddress } from "@/lib/account/account.functions";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { MapPin, Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_account/account/addresses")({
  component: AddressesPage,
});

type Address = {
  id?: string;
  label?: string | null;
  full_name: string;
  company?: string | null;
  phone?: string | null;
  line1: string;
  line2?: string | null;
  city: string;
  postal_code: string;
  country: string;
  is_default_shipping?: boolean;
  is_default_billing?: boolean;
};

const empty: Address = {
  full_name: "",
  line1: "",
  city: "",
  postal_code: "",
  country: "BG",
};

function AddressesPage() {
  const qc = useQueryClient();
  const list = useServerFn(listMyAddresses);
  const save = useServerFn(upsertAddress);
  const del = useServerFn(deleteAddress);
  const addrQ = useQuery({ queryKey: ["account", "addresses"], queryFn: () => list() });

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Address>(empty);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const saveM = useMutation({
    mutationFn: (a: Address) => save({ data: a as any }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "addresses"] });
      toast.success("Address saved");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });
  const delM = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["account", "addresses"] });
      toast.success("Address removed");
    },
  });

  const addresses = addrQ.data ?? [];

  function openEdit(a?: Address) {
    setDraft(a ?? empty);
    setOpen(true);
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Addresses</h1>
          <p className="text-sm text-slate-500">Save addresses for faster checkout.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openEdit()} className="bg-[#2563EB] hover:bg-[#1d4ed8]">
              <Plus className="mr-1.5 h-4 w-4" /> Add address
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{draft.id ? "Edit address" : "Add address"}</DialogTitle>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveM.mutate(draft);
              }}
              className="space-y-3"
            >
              <Field label="Label (optional)">
                <Input placeholder="Home, Work…" value={draft.label ?? ""} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
              </Field>
              <Field label="Full name">
                <Input required value={draft.full_name} onChange={(e) => setDraft({ ...draft, full_name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company (optional)">
                  <Input value={draft.company ?? ""} onChange={(e) => setDraft({ ...draft, company: e.target.value })} />
                </Field>
                <Field label="Phone">
                  <Input value={draft.phone ?? ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
                </Field>
              </div>
              <Field label="Address line 1">
                <Input required value={draft.line1} onChange={(e) => setDraft({ ...draft, line1: e.target.value })} />
              </Field>
              <Field label="Address line 2 (optional)">
                <Input value={draft.line2 ?? ""} onChange={(e) => setDraft({ ...draft, line2: e.target.value })} />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="City">
                  <Input required value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
                </Field>
                <Field label="Postal code">
                  <Input required value={draft.postal_code} onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })} />
                </Field>
                <Field label="Country (2-letter)">
                  <Input
                    required
                    maxLength={2}
                    value={draft.country}
                    onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })}
                  />
                </Field>
              </div>
              <div className="flex flex-wrap gap-4 pt-2">
                <Checkbox
                  label="Default shipping"
                  checked={!!draft.is_default_shipping}
                  onChange={(v) => setDraft({ ...draft, is_default_shipping: v })}
                />
                <Checkbox
                  label="Default billing"
                  checked={!!draft.is_default_billing}
                  onChange={(v) => setDraft({ ...draft, is_default_billing: v })}
                />
              </div>
              <Button type="submit" disabled={saveM.isPending} className="mt-2 w-full bg-[#2563EB] hover:bg-[#1d4ed8]">
                {saveM.isPending ? "Saving…" : "Save address"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {addrQ.isLoading ? (
        <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500">Loading…</div>
      ) : addresses.length === 0 ? (
        <div className="rounded-2xl border bg-white p-10 text-center">
          <MapPin className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-900">No addresses saved</p>
          <p className="text-sm text-slate-500">Add an address to speed up checkout.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {addresses.map((a: any) => (
            <div key={a.id} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {a.label || "Address"}
                  </p>
                  <p className="mt-1 font-semibold text-slate-900">{a.full_name}</p>
                  {a.company && <p className="text-sm text-slate-700">{a.company}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(a)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setConfirmDeleteId(a.id);
                    }}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-700">{a.line1}</p>
              {a.line2 && <p className="text-sm text-slate-700">{a.line2}</p>}
              <p className="text-sm text-slate-700">
                {a.postal_code} {a.city}, {a.country}
              </p>
              {a.phone && <p className="mt-1 text-xs text-slate-500">{a.phone}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {a.is_default_shipping && <Badge>Default shipping</Badge>}
                {a.is_default_billing && <Badge>Default billing</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this address?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Orders already placed to it will keep their original shipping details.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmDeleteId) { delM.mutate(confirmDeleteId); setConfirmDeleteId(null); } }}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1 block text-xs font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

function Checkbox({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#2563EB]" />
      {label}
    </label>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 px-2 py-0.5 text-[11px] font-semibold text-[#2563EB]">
      <Star className="h-3 w-3" /> {children}
    </span>
  );
}